import { Alert, Button } from '@zenchef/ds-react'
import { Box, Flex, styled } from '@zenchef/styled-system/jsx'
import { token } from '@zenchef/styled-system/tokens'
import { Bar, Input, Label, LabelBox, Text, TextArea } from '@zenchef/ui'
import { isValidNumber } from 'libphonenumber-js'
import { toJS } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { useRouter } from 'next/router'
import { rgba } from 'polished'
import { useContext, useEffect } from 'react'

import { DatePicker, MainLayout } from '@/components'
import GDPRText from '@/components/redesign/Form/GDPRText'
import Checkbox from '@/components/ui/Checkbox'
import CustomTelInput from '@/components/ui/CustomTelInput'
import NumberPicker from '@/components/ui/NumberPicker'
import Radio from '@/components/ui/Radio'
import Select from '@/components/ui/Select'
import { ANALYTICS_EVENTS_NAMES, fireAnalyticEvent } from '@/lib/GoogleAnalytics'
import { Bookings, NextPageWithLayout } from '@/types/types'
import { usePhoneCountry, useTranslation } from '@/utils/hooks'
import safelySetInnerHTML from '@/utils/safelySetInnerHTML'
import StoresContext from '@/utils/StoresContext'

const ErrorMessage = (props) => {
  return (
    <Text pt='5px' fontSize='s' color='red.default'>
      {props.children}
    </Text>
  )
}

const isValidEmail = (email) => {
  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ // eslint-disable-line
  return re.test(String(email).toLowerCase())
}

const Privatisation: NextPageWithLayout = observer((props) => {
  const { t } = useTranslation()
  const { appStore } = useContext(StoresContext)
  const router = useRouter()

  const {
    language,
    wish,
    formData,
    customFieldsPrivatisation,
    error,
    restaurantId,
    theme,
    name: restaurantName
  } = appStore.state
  const { day } = wish

  const eventTypes = [
    'seminary',
    'wedding',
    'corporate event',
    'press launch',
    'family reception',
    'training',
    'birthday',
    'lunch or diner',
    'others'
  ]
  const mandatoryFields = [
    'firstname',
    'lastname',
    'phone_number',
    'email',
    'country',
    'comment',
    'moment',
    'type_client',
    'type_event',
    'budget',
    'zip',
    'eula_accepted'
  ]

  const fieldpairs = customFieldsPrivatisation.reduce<Bookings.CustomField[][]>((result, _value, index, array) => {
    if (index % 2 === 0) result.push(array.slice(index, index + 2))
    return result
  }, [])

  const formValidationError: Record<string, boolean> = useLocalObservable(() => ({
    eula_accepted: formData.eula_accepted,
    ...[
      // mandatory fields
      ...toJS(mandatoryFields),
      // custom fields are all mandatory for privatisation
      ...customFieldsPrivatisation.map((c) => `custom_field_privatisation.${c.slug}`)
    ].reduce((obj, val) => {
      obj[val] = false
      return obj
    }, {})
  }))

  const state = useLocalObservable(() => ({
    submittedWithError: false
  }))

  useEffect(() => {
    formData.eula_accepted = false
  }, [])

  const handleChangePax = (event) => {
    const pax = event.target.value
    wish.pax = pax
    fireAnalyticEvent(ANALYTICS_EVENTS_NAMES.CHOOSE_PAX, {
      pax
    })
  }

  const phoneCountry = usePhoneCountry()

  return (
    <styled.form onSubmit={handleSubmit} p='padding.4'>
      {error && <Alert function='error' {...safelySetInnerHTML(t(error))} />}
      <Flex flexWrap='wrap' mb={['0px', '15px']} justifyContent='space-between'>
        <LabelBox
          width={[1, 1 / 2]}
          pr={['0px', '5px']}
          mb={['10px', '0']}
          rightPosition={['5px', '10px']}
          position='relative'>
          <Label css='text-transform:uppercase;letter-spacing:0.5px;' fontSize='xxs' color='label.black.0' mb='5px'>
            {t('number_of_person')}
          </Label>
          <NumberPicker
            min={1}
            max={10000}
            defaultValue={1}
            p='5px 0px 5px 10px'
            value={wish.pax ?? undefined}
            width={1}
            borderRadius={1}
            border='grey.2'
            inverted
            onChange={(e) => {
              handleChangePax(e)
            }}
          />
        </LabelBox>
        <LabelBox
          width={[1, 1 / 2]}
          pl={['0px', '5px']}
          mb={['10px', '0']}
          rightPosition={['20px', '25px']}
          position='relative'
          aria-label='pax'>
          <Label
            css='text-transform:uppercase;letter-spacing:0.5px;'
            fontSize='xxs'
            color='label.black.0'
            mb='5px'
            error={formValidationError.date}
            aria-label='date'>
            {t('date')}
          </Label>
          <DatePicker
            privatisation
            handleDaySelected={(nextDay) => {
              fireAnalyticEvent(
                ANALYTICS_EVENTS_NAMES.CHOOSE_DATE,
                {
                  date: nextDay
                },
                'privatisation'
              )
            }}
            handleOpenCalendar={() => {
              fireAnalyticEvent(ANALYTICS_EVENTS_NAMES.OPEN_CALENDAR)
            }}
          />
        </LabelBox>
      </Flex>
      <Flex flexWrap='wrap' mb={['0px', '15px']} justifyContent='space-between'>
        <LabelBox
          width={[1, 1 / 2]}
          pr={['0px', '5px']}
          position='relative'
          rightPosition={['5px', '10px']}
          mb={['10px', '0']}>
          <Label css='text-transform:uppercase;letter-spacing:0.5px;' fontSize='xxs' color='label.black.0' mb='5px'>
            {t('email')}
          </Label>
          <Input
            height={[3, 2]}
            color='black.cool'
            value={formData.email}
            p='5px 10px'
            width={1}
            borderRadius={1}
            border='grey.2'
            name='email'
            type='email'
            onChange={(e) => {
              changeAttribute('email', e.target.value)
            }}
            onBlur={() => handleBlur('email')}
            error={formValidationError.email}
          />
          {formValidationError.email && formData.email !== '' && (
            <ErrorMessage mt='5px'> {t('invalid_format')} </ErrorMessage>
          )}
        </LabelBox>
        <LabelBox
          as='div'
          width={[1, 1 / 2]}
          pl={['0px', '5px']}
          rightPosition={['5px']}
          mb={['10px', '0']}
          position='relative'>
          <Label css='text-transform:uppercase;letter-spacing:0.5px;' fontSize='xxs' color='label.black.0' mb='5px'>
            {t('telephone')}
          </Label>
          <CustomTelInput
            defaultCountry={phoneCountry}
            name='phone_number'
            value={formData.phone}
            onPhoneNumberChange={(_isValid, value, countryIso, valueFormatted) => {
              if (formData.phone_number === value) return

              changeAttribute('country', countryIso)
              handleChangePhoneNumber(value, valueFormatted, 'phone')
            }}
            onBlur={() => handleBlur('phone_number')}
            error={formValidationError.phone_number}
          />
          {formValidationError.phone_number && formData.phone !== '' && (
            <ErrorMessage mt='5px'> {t('invalid_format')} </ErrorMessage>
          )}
        </LabelBox>
      </Flex>
      <Flex flexWrap='wrap' mb={['0px', '15px']} justifyContent='space-between'>
        <LabelBox
          as='div'
          width={[1, 1 / 2]}
          pr={['0px', '5px']}
          mb={['10px', '0']}
          rightPosition={['5px', '10px']}
          position='relative'>
          <Label css='text-transform:uppercase;letter-spacing:0.5px;' fontSize='xxs' color='label.black.0' mb='5px'>
            {t('firstname')}
          </Label>
          <Input
            height={[3, 2]}
            p='5px 10px'
            value={formData.firstname}
            width={1}
            borderRadius={1}
            border='grey.2'
            color='black.cool'
            name='firstname'
            onChange={(e) => {
              changeAttribute('firstname', e.target.value)
            }}
            onBlur={() => {
              handleBlur('firstname')
            }}
            error={formValidationError.firstname}
          />
        </LabelBox>
        <LabelBox
          as='div'
          width={[1, 1 / 2]}
          pl={['0px', '5px']}
          rightPosition='5px'
          position='relative'
          mb={['10px', '0']}>
          <Label css='text-transform:uppercase;letter-spacing:0.5px;' fontSize='xxs' color='label.black.0' mb='5px'>
            {t('lastname')}
          </Label>
          <Input
            height={[3, 2]}
            color='black.cool'
            p='5px 10px'
            name='lastname'
            value={formData.lastname}
            width={1}
            borderRadius={1}
            border='grey.2'
            onChange={(e) => {
              changeAttribute('lastname', e.target.value)
            }}
            onBlur={() => {
              handleBlur('lastname')
            }}
            error={formValidationError.lastname}
          />
        </LabelBox>
      </Flex>
      <Flex flexWrap='wrap' mb={['0px', '15px']} justifyContent='space-between'>
        <LabelBox
          as='div'
          width={[1, 1 / 2]}
          pr={['0px', '5px']}
          mb={['10px', '0']}
          rightPosition={['5px', '10px']}
          position='relative'>
          <Label css='text-transform:uppercase;letter-spacing:0.5px;' fontSize='xxs' color='label.black.0' mb='5px'>
            {t('event_type')}
          </Label>
          <Select
            color='black.cool'
            width={1}
            value={formData.type_event}
            onChange={(e) => {
              changeAttribute('type_event', e.target.value)
            }}
            onBlur={() => {
              handleBlur('type_event')
            }}
            error={formValidationError.type_event}>
            <option />
            {eventTypes.map((option) => (
              <option key={`event_type_${option}`} value={option}>
                {t(option)}
              </option>
            ))}
          </Select>
        </LabelBox>
        <LabelBox width={[1, 1 / 2]} pl={['0px', '5px']} rightPosition='5px' position='relative' mb={['10px', '0']}>
          <Label css='text-transform:uppercase;letter-spacing:0.5px;' fontSize='xxs' color='label.black.0' mb='5px'>
            {t('budget')}
          </Label>
          <Input
            height={[3, 2]}
            color='black.cool'
            p='5px 10px'
            name='budget'
            value={formData.budget}
            width={1}
            borderRadius={1}
            border='grey.2'
            onChange={(e) => {
              changeAttribute('budget', e.target.value)
            }}
            onBlur={() => {
              handleBlur('budget')
            }}
            error={formValidationError.budget}
          />
        </LabelBox>
      </Flex>
      <Flex flexWrap='wrap' mb={['0px', '15px']} justifyContent='space-between'>
        <LabelBox
          width={[1, 1 / 2]}
          pr={['0px', '5px']}
          mb={['10px', '0']}
          rightPosition={['5px', '10px']}
          position='relative'>
          <Label css='text-transform:uppercase;letter-spacing:0.5px;' fontSize='xxs' color='label.black.0' mb='5px'>
            {t('postal_code')}
          </Label>
          <Input
            height={[3, 2]}
            color='black.cool'
            p='5px 10px'
            value={formData.zip}
            width={1}
            borderRadius={1}
            border='grey.2'
            name='zip'
            onChange={(e) => {
              changeAttribute('zip', e.target.value)
            }}
            onBlur={() => {
              handleBlur('zip')
            }}
            error={formValidationError.zip}
          />
        </LabelBox>
      </Flex>
      <Flex mb={['0px', '15px']} flexWrap='wrap' flexDirection='row' justifyContent='space-between'>
        <Flex mb={['10px', '0']} width={['100%', '50%']} flexDirection='column'>
          <Label css='text-transform:uppercase;letter-spacing:0.5px;' fontSize='xxs' color='label.black.0' mb='5px'>
            {t('moment')}
          </Label>
          <Flex justifyContent='space-between' flexDirection='row'>
            <Label width={1} fontWeight={0}>
              <Radio
                testId='moment-day'
                mr='8px'
                name='moment'
                value='day'
                checked={formData.moment === 'day'}
                onChange={(e) => {
                  changeAttribute('moment', e.target.value)
                }}
                onBlur={() => {
                  handleBlur('moment')
                }}
                error={formValidationError.moment}
              />
              <Text color={formValidationError.moment ? 'red.default' : 'black.cool'}>{t('day')}</Text>
            </Label>
            <Label width={1} fontWeight={0}>
              <Radio
                testId='moment-evening'
                mr='8px'
                name='moment'
                value='evening'
                checked={formData.moment === 'evening'}
                onChange={(e) => {
                  changeAttribute('moment', e.target.value)
                }}
                onBlur={() => {
                  handleBlur('moment')
                }}
                error={formValidationError.moment}
              />
              <Text color={formValidationError.moment ? 'red.default' : 'black.cool'}>{t('evening')}</Text>
            </Label>
          </Flex>
        </Flex>
        <Flex mb={['10px', '0']} width={['100%', '50%']} flexDirection='column'>
          <Label css='text-transform:uppercase;letter-spacing:0.5px;' fontSize='xxs' color='label.black.0' mb='5px'>
            {t('event')}
          </Label>
          <Flex justifyContent='space-between' flexDirection='row'>
            <Label width={1} fontWeight={0}>
              <Radio
                mr='8px'
                name='type_client'
                value='private'
                testId='type_client-private'
                checked={formData.type_client === 'private'}
                onChange={(e) => {
                  changeAttribute('type_client', e.target.value)
                }}
                error={formValidationError.type_client}
              />
              <Text color={formValidationError.type_client ? 'red.default' : 'black.cool'}>{t('private')}</Text>
            </Label>
            <Label width={1} fontWeight={0}>
              <Radio
                mr='8px'
                name='type_client'
                value='professional'
                testId='type_client-professional'
                checked={formData.type_client === 'professional'}
                onChange={(e) => {
                  changeAttribute('type_client', e.target.value)
                }}
                error={formValidationError.type_client}
              />
              <Text color={formValidationError.type_client ? 'red.default' : 'black.cool'}>{t('professional')}</Text>
            </Label>
          </Flex>
        </Flex>
      </Flex>
      <Flex flexWrap='wrap' mb={['0px', '15px']} justifyContent='space-between'>
        <Box width={['100%']} mb={['10px', '0']}>
          <Label css='text-transform:uppercase;letter-spacing:0.5px;' fontSize='xxs' color='label.black.0' mb='5px'>
            {t('privatisation_form_comment')}
          </Label>
          <TextArea
            as='textarea'
            color='black.cool'
            p='5px 10px'
            height={[3, 2]}
            name='comment'
            value={formData.comment}
            onChange={(e) => {
              changeAttribute('comment', e.target.value)
            }}
            onBlur={() => {
              handleBlur('comment')
            }}
            error={formValidationError.comment}
          />
        </Box>
      </Flex>
      {customFieldsPrivatisation && (
        <>
          <Bar bg='grey.separator' mx={0} />
          {fieldpairs &&
            fieldpairs.map((fieldpair, pairindex) => {
              return (
                <Flex
                  key={`fieldpair_${pairindex}`}
                  flexWrap='wrap'
                  mb={['0px', '15px']}
                  justifyContent='space-between'
                  alignItems='center'>
                  {fieldpair.map((field, index) => {
                    return (
                      <Box
                        key={`${field.slug}_${index}`}
                        width={fieldpair.length > 1 ? ['100%', '50%'] : '100%'}
                        pr={index === 0 && fieldpair.length > 1 ? ['0px', '5px'] : ''}
                        pl={index === 1 && fieldpair.length > 1 ? ['0px', '5px'] : ''}
                        mb={
                          (field.displayType === 'select' || field.displayType === 'text') && fieldpair.length > 1
                            ? ['10px', '0px']
                            : ['0px']
                        }>
                        {field.displayType === 'radio' && (
                          <>
                            <Flex flexDirection='row'>
                              <Label
                                css='text-transform:uppercase;letter-spacing:0.5px;'
                                fontSize='xxs'
                                color='label.black.0'
                                mb='5px'
                                height='auto'>
                                {field.title}
                              </Label>
                              {!appStore.isRequired(`custom_field_privatisation.${field.slug}`) && (
                                <Label
                                  fontWeight={0}
                                  fontSize='xxs'
                                  ml='3px'
                                  css='letter-spacing:0.5px;'
                                  color='label.black.0'
                                  mb='5px'
                                  height='auto'>
                                  {t('optional_fields')}
                                </Label>
                              )}
                            </Flex>
                            <Flex flexDirection='row' mb={['0px', '15px']} justifyContent='space-between'>
                              {field.options &&
                                field.options.map((option, index) => {
                                  return (
                                    <LabelBox
                                      as='div'
                                      pr={['0px', '5px']}
                                      rightPosition={['5px', '10px']}
                                      topPosition={['2.1em', '20px']}
                                      mb={['10px', '0px']}
                                      position='relative'
                                      key={`radio_option_${index}`}>
                                      <Label
                                        width={[1, 1 / field.options.length]}
                                        fontWeight={0}
                                        color={
                                          formValidationError[`custom_field_privatisation.${field.slug}`]
                                            ? 'red.default'
                                            : 'black.cool'
                                        }
                                        fontSize='s'
                                        mb='5px'>
                                        <Radio
                                          mr='8px'
                                          testId={`custom_field_privatisation-${option.value}`}
                                          name={field.slug}
                                          value={option.value}
                                          checked={formData.custom_field[field.slug] === option.value}
                                          onChange={(e) => changeCustomFieldAttribute(field.slug, e.target.value)}
                                          error={formValidationError[`custom_field_privatisation.${field.slug}`]}
                                          onBlur={() => {
                                            appStore.isRequired(`custom_field_privatisation.${field.slug}`) &&
                                              handleBlur(`custom_field_privatisation.${field.slug}`)
                                          }}
                                        />
                                        {option.text}
                                      </Label>
                                    </LabelBox>
                                  )
                                })}
                            </Flex>
                          </>
                        )}
                        {field.displayType === 'text' && (
                          <LabelBox
                            as='div'
                            pr={['0px', '5px']}
                            rightPosition={['5px', '10px']}
                            topPosition={['2.1em', '20px']}
                            mb={['10px', '0']}
                            position='relative'>
                            <Flex>
                              <Label
                                css='text-transform:uppercase;letter-spacing:0.5px;'
                                fontSize='xxs'
                                color='label.black.0'
                                mb='5px'>
                                {field.title}
                              </Label>
                              {!appStore.isRequired(`custom_field_privatisation.${field.slug}`) && (
                                <Label
                                  fontWeight={0}
                                  fontSize='xxs'
                                  ml='3px'
                                  css='letter-spacing:0.5px;'
                                  color='label.black.0'
                                  mb='5px'
                                  height='auto'>
                                  {t('optional_fields')}
                                </Label>
                              )}
                            </Flex>
                            <LabelBox
                              mb={['10px', '0']}
                              rightPosition={['5px', '10px']}
                              topPosition={['2.1em', '20px']}>
                              <Input
                                width={1}
                                color='black.cool'
                                height={[3, 2]}
                                p='5px 10px'
                                borderRadius={1}
                                border='grey.2'
                                error={formValidationError[`custom_field_privatisation.${field.slug}`]}
                                value={formData.custom_field[field.slug] || ''}
                                onChange={(e) => {
                                  changeCustomFieldAttribute(field.slug, e.target.value)
                                }}
                                onBlur={() => {
                                  appStore.isRequired(`custom_field_privatisation.${field.slug}`) &&
                                    handleBlur(`custom_field_privatisation.${field.slug}`)
                                }}
                              />
                            </LabelBox>
                          </LabelBox>
                        )}
                        {field.displayType === 'checkbox' && (
                          <LabelBox
                            as='div'
                            pr={['0px', '5px']}
                            rightPosition={['5px', '10px']}
                            topPosition={['2.1em', '20px']}
                            mb={['10px', '0']}
                            position='relative'>
                            <Label
                              noLineHeight
                              width={1}
                              alignItems='center'
                              pt={['0px', '10px']}
                              fontSize='s'
                              height='auto'
                              fontWeight={0}
                              color='black.cool'>
                              <Checkbox
                                mr='8px'
                                checked={formData.custom_field[field.slug] || false}
                                onChange={(e) => {
                                  changeCustomFieldAttribute(field.slug, e.target.checked)
                                  if (
                                    !e.target.checked &&
                                    appStore.isRequired(`custom_field_privatisation.${field.slug}`)
                                  ) {
                                    handleBlur(`custom_field_privatisation.${field.slug}`)
                                  }
                                }}
                                error={formValidationError[`custom_field_privatisation.${field.slug}`]}
                              />
                              <Flex
                                flexDirection='row'
                                style={{
                                  color: formValidationError[`custom_field_privatisation.${field.slug}`]
                                    ? theme.colors.red.default
                                    : ''
                                }}>
                                <Label fontWeight={0}>{t(field.title)}</Label>
                                {!appStore.isRequired(`custom_field_privatisation.${field.slug}`) && (
                                  <Label
                                    fontWeight={0}
                                    fontSize='xxs'
                                    ml='3px'
                                    css='letter-spacing:0.5px;'
                                    color='label.black.0'
                                    height='auto'>
                                    {t('optional_fields')}
                                  </Label>
                                )}
                              </Flex>
                            </Label>
                          </LabelBox>
                        )}
                        {field.displayType === 'select' && (
                          <LabelBox as='div' pr={['0px', '5px']} mb={['10px', '0']} position='relative'>
                            <Flex flexDirection='row'>
                              <Label
                                css='text-transform:uppercase;letter-spacing:0.5px;'
                                fontSize='xxs'
                                color='label.black.0'
                                mb='5px'>
                                {t(field.title)}
                              </Label>
                              {!appStore.isRequired(`custom_field_privatisation.${field.slug}`) && (
                                <Label
                                  fontWeight={0}
                                  fontSize='xxs'
                                  ml='3px'
                                  css='letter-spacing:0.5px;'
                                  color='label.black.0'
                                  mb='5px'
                                  height='auto'>
                                  {t('optional_fields')}
                                </Label>
                              )}
                            </Flex>
                            <LabelBox rightPosition={['5px', '10px']} topPosition={['2.1em', '20px']}>
                              <Select
                                color='grey.1'
                                border='grey.2'
                                width={1}
                                value={(formData.custom_field[field.slug] && formData.custom_field[field.slug]) || ''}
                                onChange={(e) => {
                                  changeCustomFieldAttribute(field.slug, e.target.value)
                                  validate(`custom_field_privatisation.${field.slug}`)
                                }}
                                onBlur={() => {
                                  appStore.isRequired(`custom_field_privatisation.${field.slug}`) &&
                                    handleBlur(`custom_field_privatisation.${field.slug}`)
                                }}
                                error={formValidationError[`custom_field_privatisation.${field.slug}`]}>
                                <option value='' />
                                {field.options.map((option, index) => {
                                  return (
                                    <option value={option.value} key={`${field.slug}_${index}_option`}>
                                      {t(option.text)}
                                    </option>
                                  )
                                })}
                              </Select>
                            </LabelBox>
                          </LabelBox>
                        )}
                      </Box>
                    )
                  })}
                </Flex>
              )
            })}
        </>
      )}
      <Box bg='background.neutral.base.subtler' p='padding.2' mb='gap.4' borderRadius='l'>
        <Flex flexDirection='column'>
          <Label noLineHeight fontSize='s' color='black.cool' fontWeight={0} htmlFor='save_info'>
            <Checkbox
              id='save_info'
              mr='8px'
              checked={formData.save_info}
              onChange={(e) => {
                changeAttribute('save_info', e.target.checked)
              }}
            />
            <Label fontWeight={0} htmlFor='save_info'>
              {t('save_info')}
            </Label>
          </Label>

          <Label noLineHeight mt='10px' fontSize='s' color='black.cool' fontWeight={0} htmlFor='eula_accepted'>
            <Checkbox
              id='eula_accepted'
              error={formValidationError.eula_accepted}
              mr='8px'
              checked={formData.eula_accepted}
              onChange={(e) => {
                changeAttribute('eula_accepted', e.target.checked)
                if (!e.target.checked) {
                  handleBlur('eula_accepted')
                }
              }}
            />
            <Text fontWeight={0}>
              <span
                style={{ color: formValidationError.eula_accepted ? theme.colors.red.default : '' }}
                {...safelySetInnerHTML(
                  t('accept_eula', {
                    hyperlink: `/gtc?rid=${restaurantId}&lang=${language}`,
                    hyperlinkIconColor: token.var('colors.background.brand.adaptive'),
                    requiredColor: rgba(theme.colors.red.default, 0.8)
                  })
                )}
              />
            </Text>
          </Label>
        </Flex>
      </Box>
      {state.submittedWithError && Object.values(formValidationError).some((error) => error) && (
        <Alert
          function='error'
          title={t('please_fill')}
          description={Object.keys(formValidationError)
            .map((errorField) => {
              if (formValidationError[errorField]) {
                if (errorField.slice(0, 26) === 'custom_field_privatisation') {
                  return ` • ${
                    customFieldsPrivatisation.find((customField) => customField.slug === errorField.slice(27))?.title
                  }`
                } else if (errorField === 'eula_accepted') {
                  return `${t('eula')}`
                } else if (errorField === 'country' || errorField === undefined) {
                  return ''
                } else if (errorField === 'type_client') {
                  return ` • ${t('event_type')}`
                } else if (errorField === 'zip') {
                  return ` • ${t('postal_code')}`
                } else if (errorField === 'type_event') {
                  return ` • ${t('event_type')}`
                } else if (errorField === 'phone_number') {
                  return ` • ${t('telephone')}`
                } else if (errorField === 'comment') {
                  return ` • ${t('privatisation_form_comment')}`
                } else {
                  return errorField ? ` • ${t(errorField)}` : ''
                }
              }
            })
            .join('')}
        />
      )}
      <Flex justifyContent='center' m='17px'>
        <Button type='submit' hierarchy='primary-bold' size='small' testId='validate_privatisation'>
          {t('validate')}
        </Button>
      </Flex>
      <Flex flexDirection='column'>
        <GDPRText />
      </Flex>
    </styled.form>
  )

  function canSend() {
    if (Object.values(formValidationError).some((invalid) => invalid)) {
      state.submittedWithError = true
      return false
    }

    const values = mandatoryFields.reduce((result, value) => {
      return result + (formData[value] && formData[value] !== '' ? 1 : 0)
    }, 0)
    return day && values === mandatoryFields.length && !isNaN(wish.pax) && wish.pax >= 1
  }

  function changeAttribute(attribute, value) {
    formData[attribute] = value
    if (formValidationError[attribute]) {
      formValidationError[attribute] = false
    }
  }

  function handleBlur(field) {
    validate(field)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    for (const field of Object.keys(formValidationError)) {
      validate(field)
    }
    if (!canSend()) {
      return
    }

    const privatisation = await appStore.createPrivatisation()

    if (!error && privatisation) {
      sendPrivatisationCreatedEvent()
      router.push({ pathname: '/thank_you_privatisation' })
    }
  }

  function sendPrivatisationCreatedEvent() {
    const { restaurantId, wish, partner_id, formData } = appStore.state
    fireAnalyticEvent(
      ANALYTICS_EVENTS_NAMES.BOOKING_CREATED,
      {
        restaurant_id: restaurantId,
        pax: wish.pax,
        date: wish.day,
        time: wish.slot,
        partner_id,
        booking_type: 'privatisation',
        optin_mail:
          formData.optins && formData.optins.find((optin) => optin.type === 'market_mail')
            ? formData.optins.find((optin) => optin.type === 'market_mail')?.value
            : 0,
        optin_sms:
          formData.optins && formData.optins.find((optin) => optin.type === 'market_sms')
            ? formData.optins.find((optin) => optin.type === 'market_sms')?.value
            : 0
      },
      'privatisation'
    )
  }

  function validate(field) {
    let valid
    switch (field) {
      case 'firstname':
      case 'lastname':
      case 'budget':
      case 'zip':
      case 'comment':
      case 'date':
      case 'type_event':
      case 'type_client':
        valid = formData[field].length >= 1
        break
      case 'email':
        valid = isValidEmail(formData[field])
        break
      case 'phone_number':
        valid = isValidNumber(formData[field])
        break
      case 'country':
        valid = formData[field].length === 2 && /[a-zA-Z]/.test(formData[field])
        break
      case 'eula_accepted':
      default:
        if (field.split('.')[0] === 'custom_field_privatisation') {
          valid = formData.custom_field[field.split('.')[1]]
          break
        } else {
          valid = formData[field]
          break
        }
    }
    formValidationError[field] = !valid
  }

  function handleChangePhoneNumber(printedNumber, formattedNumber, attribute) {
    changeAttribute(`tmp_${attribute}`, printedNumber)
    changeAttribute(attribute, formattedNumber)
    changeAttribute('phone_number', formattedNumber)
  }

  function changeCustomFieldAttribute(attr, value) {
    formData.custom_field = {
      ...formData.custom_field,
      [attr]: value
    }
  }
})

Privatisation.getLayout = (page) => <MainLayout>{page}</MainLayout>

export default Privatisation
